import dayjs from 'dayjs';
import { isCurrentUserTopOfferer } from '../Context/backend-driven-offer-helpers';
import { useVehicleCardContext } from '../Context/vehicle-card.context';
import { MARKETPLACE_TAB } from 'constants/marketplace';
import { isExpired, isSold } from 'helpers/vehicle';
import { useBuyerNewOfferPayloadStruct } from 'helpers/split/hooks';

export const getTags = ({ badges }) => badges?.map(({ key }) => key);

export const isMarketplace = ({ sale_type, offerData }) =>
	['marketplace', 'private_marketplace_sale'].includes(offerData?.public?.general?.sale_type || sale_type);

export const isAuctionStyleEndVehicle = (vehicle) => Boolean(vehicle?.auction_style_end);

export const hasAcceptableCounterOffer = (vehicle) => Boolean(vehicle?.acceptable_counter_offer_activity);

export const hasStarted = ({ bid_sale_auction_starts_at }, overrideDateNow) =>
	dayjs(bid_sale_auction_starts_at).isBefore(dayjs(overrideDateNow));

export const hasEnded = (vehicle, overrideDateNow) => {
	const { bid_sale_auction_ends_at } = vehicle;
	return (
		(Boolean(bid_sale_auction_ends_at) && dayjs(bid_sale_auction_ends_at).isBefore(dayjs(overrideDateNow))) ||
		isSold(vehicle) ||
		isExpired(vehicle)
	);
};

export const isOnExtension = (vehicle, overrideDateNow) => {
	const { bid_sale_auction_extended_until } = vehicle;
	return (
		Boolean(bid_sale_auction_extended_until) &&
		dayjs(bid_sale_auction_extended_until).isAfter(dayjs(overrideDateNow)) &&
		!isSold(vehicle) &&
		!isExpired(vehicle)
	);
};

export const didUserAlreadyBid = ({ my_offer_amount }) => Boolean(my_offer_amount);

const isOtherUserFromMyDealership = ({ last_offer_user, user }) => {
	return Boolean(last_offer_user?.id) && last_offer_user?.id !== user.id;
};

const formatUserAtDealership = ({ userName, dealershipName }) => {
	const [first, second] = userName.split(' ');
	const formattedName = second ? `${first} ${second.charAt(0)}.` : first;
	return `${formattedName} at ${dealershipName}`;
};
export const getMyDealershipName = ({
	vehicle: { offerer_name, last_offer_user },
	user,
	masterOnBehalfOf,
	default: defaultDealershipName,
}) => {
	if (masterOnBehalfOf) return offerer_name;
	if (isOtherUserFromMyDealership({ last_offer_user, user })) {
		return formatUserAtDealership({ userName: last_offer_user.name, dealershipName: offerer_name });
	}
	return defaultDealershipName || user.dealership_name;
};

const appendFirmText = (name, firm) => (firm ? `${name} (firm)` : name);
export const getCounterBuyerText = (
	{
		offerer_name,
		my_offer_date,
		bid_sale_auction_extended_until,
		bid_sale_auction_ends_at,
		my_offer_firm,
		last_offer_user,
	},
	masterOnBehalfOf,
	user,
) => {
	const buyerHasCounterOffer = dayjs(my_offer_date).isAfter(
		bid_sale_auction_extended_until || bid_sale_auction_ends_at,
	);

	if (masterOnBehalfOf) {
		return appendFirmText(offerer_name, my_offer_firm);
	} else if (isOtherUserFromMyDealership({ last_offer_user, user })) {
		return appendFirmText(
			formatUserAtDealership({ userName: last_offer_user?.name, dealershipName: user.dealership_name }),
			my_offer_firm,
		);
	} else {
		if (my_offer_firm) {
			return appendFirmText('You', true);
		}
		return buyerHasCounterOffer ? 'You countered' : 'Your high bid';
	}
};

export const getCounterSellerText = ({ lowest_counter_amount, lowest_counter_firm }) => {
	const sellerCounterOffer = !!lowest_counter_amount;
	if (sellerCounterOffer) {
		return lowest_counter_firm ? 'Seller (firm)' : 'Seller countered';
	} else {
		return 'Waiting for seller';
	}
};

export const buyerHasMaxBid = ({ my_offer_max_bid_amount }) => !!my_offer_max_bid_amount;

export const buyerHasOffer = ({ my_offer_amount }) => !!my_offer_amount;

export const sellerIsLastCounter = ({ lowest_counter_date, best_offer_date }) =>
	dayjs(lowest_counter_date).isAfter(best_offer_date);

export const sellerCounterAfterCurrentBuyer = ({ lowest_counter_date, my_offer_date }) =>
	dayjs(lowest_counter_date).isAfter(my_offer_date);

export const buyerHasCounterOfferOnAuction = ({
	bid_sale_auction_extended_until,
	bid_sale_auction_ends_at,
	my_offer_date,
}) => dayjs(my_offer_date).isAfter(bid_sale_auction_extended_until || bid_sale_auction_ends_at);

export const sellerHasCounterOffer = ({ lowest_counter_date }) => Boolean(lowest_counter_date);

export const isSearchTabAndIsNotVdp = (tab, isVdp) =>
	(tab === MARKETPLACE_TAB.marketplace || tab === MARKETPLACE_TAB.hidden) && !isVdp;

const getIsOnExclusivityPeriodLegacy = ({ vehicle, overrideDateNow, currentUserTopOfferer }) => {
	const { exclusivity_period, exclusivity_period_expires_at } = vehicle;
	const exclusivityPeriodOnOtherUser =
		!currentUserTopOfferer &&
		exclusivity_period &&
		dayjs(overrideDateNow).isBefore(dayjs(exclusivity_period_expires_at));

	return exclusivityPeriodOnOtherUser;
};

export const useIsExclusivityPeriodOnOtherUser = () => {
	const {
		overrideDateNow,
		vehicle,
		currentUserTopOfferer,
		isOnExclusivityPeriod: backendDrivenExclusivityPeriod,
	} = useVehicleCardContext();
	const isBackendDriven = useBuyerNewOfferPayloadStruct();

	if (!isBackendDriven) return getIsOnExclusivityPeriodLegacy({ vehicle, overrideDateNow, currentUserTopOfferer });

	return backendDrivenExclusivityPeriod && !isCurrentUserTopOfferer(vehicle.offerData);
};
